import { useCallback, useEffect, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import {
    addNowPlayingListener,
    canUseMusicListener,
    getNowPlayingState,
    openNowPlayingSettings,
    type NowPlayingTrack,
} from '../../native/music-listener-native';

export interface UseNowPlayingState {
    supported: boolean;
    enabled: boolean;
    loading: boolean;
    current: NowPlayingTrack | null;
    refresh: () => Promise<void>;
    openSettings: () => Promise<void>;
}

export function useNowPlaying(): UseNowPlayingState {
    const supported = canUseMusicListener();
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(supported);
    const [current, setCurrent] = useState<NowPlayingTrack | null>(null);

    const applyState = useCallback((state: { enabled: boolean; current: NowPlayingTrack | null }) => {
        setEnabled(state.enabled);
        setCurrent(state.enabled ? state.current : null);
    }, []);

    const refresh = useCallback(async () => {
        if (!supported) {
            setEnabled(false);
            setCurrent(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            applyState(await getNowPlayingState());
        } finally {
            setLoading(false);
        }
    }, [applyState, supported]);

    const openSettings = useCallback(async () => {
        await openNowPlayingSettings();
    }, []);

    useEffect(() => {
        if (!supported) {
            setLoading(false);
            return;
        }

        let disposed = false;
        let removeNowPlaying: (() => void) | null = null;
        let removeAppState: (() => void) | null = null;

        void refresh();

        void addNowPlayingListener((state) => {
            if (disposed) return;
            applyState(state);
        }).then((remove) => {
            if (disposed) remove();
            else removeNowPlaying = remove;
        });

        void CapApp.addListener('appStateChange', (state) => {
            if (state.isActive) void refresh();
        }).then((handle) => {
            removeAppState = () => { void handle.remove(); };
            if (disposed) removeAppState();
        });

        return () => {
            disposed = true;
            removeNowPlaying?.();
            removeAppState?.();
        };
    }, [applyState, refresh, supported]);

    return {
        supported,
        enabled,
        loading,
        current,
        refresh,
        openSettings,
    };
}
